import { useEffect, useRef, useState } from 'react';
import { Reveal, SectionHead } from './UI';
import SplitText from './SplitText';

const ROWS = [
  ['Works with the cameras you already have', true, false, true],
  ['Purpose-trained for industrial floors', false, 'part', true],
  ['Generates audit-ready reports automatically', false, 'part', true],
  ['One view across ops, compliance & security', false, 'part', true],
  ['Real-time alerts, not just recordings', false, 'part', true],
  ['Scales across sites from one dashboard', 'part', false, true],
  ['No rip-and-replace hardware cost', true, false, true],
];

const COLS = ['Traditional CCTV', 'Generic IoT Cameras', 'TruVue AI'];

const BG_FROM = '#FAF5FF';
const BG_TO = '#EDE9FE';
const FEATURED_BG = '#FFFFFF';
const BORDER_C = '#DDD6FE';
const TEXT_C = '#0F172A';
const HEAT = '#7C3AED';

function Check() {
  return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#DCFCE7', border: '1.5px solid #86EFAC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}><path d="M5 12l4 4 10-10" /></svg>
    </div>
  );
}
function Cross() {
  return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#FEE2E2', border: '1.5px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
    </div>
  );
}
function Dash() {
  return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#FEF3C7', border: '1.5px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}><line x1="7" y1="12" x2="17" y2="12" /></svg>
    </div>
  );
}
function cell(v) {
  return v === true ? <Check /> : v === false ? <Cross /> : <Dash />;
}

export default function WhyTruVue() {
  const scrollRef      = useRef(null);
  const gridRef        = useRef(null);
  const featuredColRef = useRef(null);
  const trackRef       = useRef(null);
  const thumbRef       = useRef(null);

  const [featuredBox, setFeaturedBox] = useState({ left: 0, width: 0, display: 'none' });
  const [thumbBox,    setThumbBox]    = useState({ width: '0%', left: 0, display: 'none' });
  const [isMobile,    setIsMobile]    = useState(false);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 960);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const rowCount = ROWS.length;

  const positionFeatured = () => {
    const grid = gridRef.current;
    const col  = featuredColRef.current;
    if (!grid || !col) return;
    const header = col.querySelector('[data-role="header"]');
    if (!header) return;
    const gridRect   = grid.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    setFeaturedBox({ left: headerRect.left - gridRect.left, width: headerRect.width, display: 'block' });
  };

  const updateScrollbar = () => {
    const el    = scrollRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const ratio = el.clientWidth / el.scrollWidth;
    if (ratio >= 1) { setThumbBox(b => ({ ...b, display: 'none' })); return; }
    const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    const maxLeft = track.clientWidth - track.clientWidth * ratio;
    setThumbBox({ width: ratio * 100 + '%', left: scrollRatio * maxLeft, display: 'block' });
  };

  useEffect(() => {
    positionFeatured();
    updateScrollbar();
    const onResize = () => { positionFeatured(); updateScrollbar(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onScroll = () => { positionFeatured(); updateScrollbar(); };

  // ── Drag to scroll — MOBILE ONLY ──
  const dragState = useRef({ down: false, startX: 0, startScroll: 0 });

  const onMouseDown = (e) => {
    if (!isMobile) return; // desktop: no drag
    dragState.current = { down: true, startX: e.clientX, startScroll: scrollRef.current.scrollLeft };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState.current.down || !scrollRef.current || !isMobile) return;
      const dx = e.clientX - dragState.current.startX;
      scrollRef.current.scrollLeft = dragState.current.startScroll - dx;
    };
    const onUp = () => { dragState.current.down = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isMobile]);

  // ── Draggable thumb — MOBILE ONLY ──
  const thumbDrag = useRef({ dragging: false, startX: 0, startLeft: 0 });

  const onThumbPointerDown = (e) => {
    if (!isMobile) return;
    thumbDrag.current = { dragging: true, startX: e.clientX, startLeft: thumbBox.left };
    e.target.setPointerCapture(e.pointerId);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!thumbDrag.current.dragging || !trackRef.current || !thumbRef.current || !scrollRef.current || !isMobile) return;
      const dx      = e.clientX - thumbDrag.current.startX;
      const track   = trackRef.current;
      const thumbW  = thumbRef.current.clientWidth;
      const maxLeft = track.clientWidth - thumbW;
      const newLeft = Math.max(0, Math.min(maxLeft, thumbDrag.current.startLeft + dx));
      const el      = scrollRef.current;
      el.scrollLeft = (newLeft / maxLeft) * (el.scrollWidth - el.clientWidth);
    };
    const onUp = () => { thumbDrag.current.dragging = false; };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [isMobile]);

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 380px) repeat(3, 1fr)',
    gridTemplateRows: `auto repeat(${rowCount}, auto)`,
    gap: 0,
    position: 'relative',
    minWidth: 780,
  };

  return (
    <section id="why-truvue" className="section section-white" style={{
      position: 'relative',
      background: `linear-gradient(180deg, ${BG_FROM}, ${BG_TO})`,
      padding: 'clamp(40px, 6vw, 80px) 12px',
    }}>
      <style>{`
        #why-truvue [class*="eyebrow"]::before,
        #why-truvue [class*="eyebrow"]::after { content: none !important; display: none !important; }
        #why-truvue .wv-outer { max-width: 1520px; }
        @media (max-width: 749px) {
          #why-truvue .wv-outer { padding: 0 4px; }
          #why-truvue .wv-scroll { padding-inline: 0; }
        }
      `}</style>

      <div className="container wv-outer" style={{ maxWidth: 1520, margin: '0 auto', paddingInline: 8 }}>
        <Reveal>
          <SectionHead
            center
            eyebrow="Why TruVue AI Wins"
            h2="Built to be used, not just installed"
            p="Most camera systems stop at recording. TruVue AI is the only layer built specifically around what industrial sites actually need."
          />
        </Reveal>

        <Reveal delay={60}>
          <div style={{ maxWidth: 1520, margin: '0 auto' }}>
            <div
              ref={scrollRef}
              onScroll={onScroll}
              onMouseDown={onMouseDown}
              className="wv-scroll"
              data-lenis-prevent
              style={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                paddingBlock: 28,
                paddingInline: 4,
                // cursor: grab only on mobile
                cursor: isMobile ? 'grab' : 'default',
              }}
            >
              <div ref={gridRef} style={gridStyle}>
                {/* corner cell */}
                <div style={{ gridRow: 1, gridColumn: 1 }} />

                {/* row labels */}
                {ROWS.map((r, i) => (
                  <div key={r[0]} style={{ gridRow: i + 2, gridColumn: 1, position: 'relative', zIndex: 1, padding: '18px 24px', display: 'flex', alignItems: 'center', fontSize: 16.5, fontWeight: 600, color: TEXT_C, borderTop: `1px solid ${BORDER_C}` }}>
                    <SplitText tag="span" text={r[0]} delay={15} duration={0.7} ease="power2.out" splitType="words" from={{ opacity: 0, y: 12 }} to={{ opacity: 1, y: 0 }} threshold={0.1} rootMargin="-40px" textAlign="left" />
                  </div>
                ))}

                {/* data columns */}
                {COLS.map((colName, colIdx) => {
                  const isFeatured = colIdx === 2;
                  const cellsForRow = ROWS.map(r => r[colIdx + 1]);
                  return (
                    <div key={colName} ref={isFeatured ? featuredColRef : null} data-col={colIdx} style={{ display: 'contents' }}>
                      <div data-role="header" style={{ gridRow: 1, gridColumn: colIdx + 2, position: 'relative', zIndex: 1, padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', textAlign: 'center', fontFamily: 'var(--font-mono, inherit)', fontWeight: 800, fontSize: colName === 'TruVue AI' ? '1.15rem' : '0.95rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: isFeatured ? HEAT : TEXT_C, borderLeft: isFeatured || colIdx === 1 ? 'none' : `1px solid ${BORDER_C}` }}>
                        <SplitText tag="span" text={colName} delay={20} duration={0.7} ease="power2.out" splitType="words" from={{ opacity: 0, y: 12 }} to={{ opacity: 1, y: 0 }} threshold={0.1} rootMargin="-40px" textAlign="center" />
                      </div>
                      {cellsForRow.map((val, rowIdx) => (
                        <div key={rowIdx} style={{ gridRow: rowIdx + 2, gridColumn: colIdx + 2, position: 'relative', zIndex: 1, padding: '15px 22px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', borderTop: `1px solid ${isFeatured ? 'rgba(124,58,237,0.15)' : BORDER_C}`, borderLeft: isFeatured || colIdx === 1 ? 'none' : `1px solid ${BORDER_C}` }}>
                          {cell(val)}
                        </div>
                      ))}
                    </div>
                  );
                })}

                {/* featured column raised background */}
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: featuredBox.left, width: featuredBox.width, display: featuredBox.display, pointerEvents: 'none', background: FEATURED_BG, borderRadius: 14, boxShadow: '0 4px 40px rgba(0,0,0,0.12)', zIndex: 0 }} />
              </div>
            </div>

            {/* custom scrollbar — mobile only */}
            {isMobile && (
              <div ref={trackRef} style={{ maxWidth: 1520, margin: '0 auto', height: 4, background: 'rgba(0,0,0,0.08)', borderRadius: 2, position: 'relative', display: thumbBox.display }}>
                <div ref={thumbRef} onPointerDown={onThumbPointerDown} style={{ height: '100%', background: 'rgba(0,0,0,0.35)', borderRadius: 2, position: 'absolute', top: 0, left: thumbBox.left, width: thumbBox.width, minWidth: 40, cursor: 'grab' }} />
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
